<template>
  <div class="ar-wrapper">

    <!-- ── iOS permission prompt (shown before anything starts) ── -->
    <div v-if="showIosPrompt" class="ios-prompt">
      <div class="ios-prompt-card">
        <div class="ios-prompt-icon">🧭</div>
        <h3 class="ios-prompt-title">{{ iosPromptText.title }}</h3>
        <p class="ios-prompt-text">
          {{ iosPromptText.body }} <strong>{{ iosPromptText.bold }}</strong> {{ iosPromptText.body2 }}
        </p>
        <p class="ios-prompt-sub">
          {{ iosPromptText.sub }} <strong>{{ iosPromptText.subBold }}</strong> {{ iosPromptText.sub2 }}
        </p>
        <button class="ios-prompt-btn" @click="handleIosPrompt">
          {{ iosPromptText.btn }}
        </button>
      </div>
    </div>

    <!-- ── Main AR content ── -->
    <template v-else>
      <!-- Camera feed -->
      <video ref="video" class="ar-video" autoplay playsinline muted></video>

      <!-- GIF overlays -->
      <div v-if="gifSrcs.length" class="ar-gifs" :class="{ 'flames-visible': facingSouth, 'ar-no-grow': pointData.arGrowAnimation === false }">
        <img
          v-for="(src, i) in gifSrcs"
          :key="i"
          :src="src"
          class="ar-gif"
          :style="i > 0 ? { marginLeft: gifSpacing + 'px' } : {}"
        />
      </div>

      <!-- Search word hint (shown until animation appears) -->
      <div v-if="!facingSouth && pointData.arSearchWord" class="ar-search-hint">
        {{ searchHintText }}
      </div>

      <!-- Direction guide (shown until animation appears) -->
      <div v-if="!facingSouth" class="ar-guide">
        <template v-if="compassAvailable">
          <!-- Proximity arc -->
          <svg class="ar-guide-arc" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="8"/>
            <circle cx="60" cy="60" r="50" fill="none" stroke="#fff" stroke-width="8"
              stroke-linecap="round"
              :stroke-dasharray="`${closeness * 3.14} 314`"
              stroke-dashoffset="78.5"
              style="transition: stroke-dasharray 0.4s ease;"/>
            <!-- Phone icon in center -->
            <text x="60" y="67" text-anchor="middle" font-size="28" fill="white">📱</text>
          </svg>
          <!-- Directional arrow -->
          <div class="ar-guide-arrow" :class="turnDirection === 'left' ? 'ar-anim-left' : 'ar-anim-right'">
            <svg v-if="turnDirection === 'left'" viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 20 H14 M14 20 L28 8 M14 20 L28 32" stroke="white" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 20 H46 M46 20 L32 8 M46 20 L32 32" stroke="white" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="ar-guide-text">{{ directionText }}</div>
        </template>
        <template v-else>
          <div class="ar-guide-scan">
            <span class="ar-scan-phone">📱</span>
          </div>
          <div class="ar-guide-text">{{ noSensorText }}</div>
        </template>
      </div>

      <!-- Error -->
      <div v-if="errorMsg" class="ar-error-bar">
        ⚠️ {{ errorMsg }}
      </div>

      <!-- AR Intro overlay -->
      <div v-if="showArIntro" class="ar-intro-overlay" @click="showArIntro = false">
        <div class="ar-intro-card" :dir="lang === 'he' || lang === 'ar' ? 'rtl' : 'ltr'">
          <div class="ar-intro-icon">📱</div>
          <p class="ar-intro-line1">{{ arIntroText.line1 }}</p>
          <p class="ar-intro-line2">{{ arIntroText.line2 }}</p>
          <button class="ar-intro-btn" @click.stop="showArIntro = false">{{ arIntroText.btn }}</button>
        </div>
      </div>

      <!-- Audio ended hint -->
      <div v-if="audioEnded" class="ar-audio-done">
        <svg class="ar-audio-done-arrow" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 48 L20 12" stroke="white" stroke-width="3.5" stroke-linecap="round"/>
          <path d="M8 22 L20 4 L32 22" stroke="white" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="ar-audio-done-text" :dir="lang === 'he' || lang === 'ar' ? 'rtl' : 'ltr'">{{ audioDoneText }}</p>
      </div>

      <!-- Audio + compass controls -->
      <div v-if="audioUrl" class="ar-audio-controls">
        <audio ref="audio" :src="audioUrl"></audio>
        <button class="ar-audio-btn" @click="togglePlay">
          {{ playing ? '⏸' : '▶️' }}
        </button>
        <button class="ar-audio-btn" @click="restartAudio">
          🔄
        </button>
      </div>
    </template>

  </div>
</template>

<script>
export default {
  name: 'ArComponent',
  props: {
    pointData: { type: Object, default: () => ({}) },
    audioUrl:  { type: String, default: '' },
    lang:      { type: String, default: 'fr' },
  },

  data() {
    return {
      gifSrcs:    [],
      gifSpacing: 0,
      errorMsg:   '',
      camStream:  null,
      playing:    false,
      facingSouth:      false,
      compassAvailable: false,
      showIosPrompt:    false,
      compassHeading:   0,
      showArIntro:      true,
      audioEnded:       false,
    };
  },

  computed: {
    turnDirection() {
      const target = this.pointData.arDirection ?? 180;
      const diff = ((this.compassHeading - target + 360) % 360);
      return diff <= 180 ? 'left' : 'right';
    },
    normalizedDiff() {
      const target = this.pointData.arDirection ?? 180;
      const diff = ((this.compassHeading - target + 360) % 360);
      return diff > 180 ? 360 - diff : diff;
    },
    closeness() {
      return Math.max(0, Math.min(100, Math.round((1 - this.normalizedDiff / 180) * 100)));
    },
    directionText() {
      const texts = {
        fr: { left: 'Tournez à gauche',       right: 'Tournez à droite',      close: 'Presque là !' },
        he: { left: 'פנה שמאלה',              right: 'פנה ימינה',             close: '!כמעט שם' },
        en: { left: 'Turn left',               right: 'Turn right',            close: 'Almost there!' },
        ar: { left: 'انعطف يساراً',           right: 'انعطف يميناً',          close: 'تقريباً هناك!' },
        de: { left: 'Nach links drehen',       right: 'Nach rechts drehen',    close: 'Fast da!' },
      };
      const t = texts[this.lang] || texts.fr;
      if (this.normalizedDiff <= 40) return t.close;
      return this.turnDirection === 'left' ? t.left : t.right;
    },
    noSensorText() {
      const texts = {
        fr: 'Bougez votre téléphone pour trouver l\'animation',
        he: 'הזיזו את הטלפון כדי למצוא את האנימציה',
        en: 'Move your phone to find the animation',
        ar: 'حرّك هاتفك للعثور على الرسوم المتحركة',
        de: 'Bewegen Sie Ihr Telefon, um die Animation zu finden',
      };
      return texts[this.lang] || texts.fr;
    },
    iosPromptText() {
      const texts = {
        fr: {
          title: 'Autorisation requise',
          body: 'Pour afficher l\'animation, l\'application a besoin d\'accéder à la',
          bold: 'boussole',
          body2: 'de votre appareil.',
          sub: 'Une fenêtre de confirmation iOS va s\'ouvrir. Si vous avez déjà refusé, rendez-vous dans',
          subBold: 'Réglages → Safari → Mouvement & orientation',
          sub2: 'pour réactiver l\'accès.',
          btn: '🧭 Autoriser la boussole',
        },
        he: {
          title: 'נדרש אישור',
          body: 'כדי להציג את האנימציה, האפליקציה זקוקה לגישה ל',
          bold: 'מצפן',
          body2: 'של המכשיר שלך.',
          sub: 'חלון אישור iOS ייפתח. אם כבר סירבת, עבור ל',
          subBold: 'הגדרות → Safari → תנועה וכיוון',
          sub2: 'כדי להפעיל מחדש את הגישה.',
          btn: '🧭 אשר גישה למצפן',
        },
        en: {
          title: 'Permission required',
          body: 'To show the animation, the app needs access to your device\'s',
          bold: 'compass',
          body2: '',
          sub: 'An iOS confirmation dialog will appear. If you already denied it, go to',
          subBold: 'Settings → Safari → Motion & Orientation',
          sub2: 'to re-enable access.',
          btn: '🧭 Allow compass',
        },
        ar: {
          title: 'إذن مطلوب',
          body: 'لعرض الرسوم المتحركة، يحتاج التطبيق إلى الوصول إلى',
          bold: 'البوصلة',
          body2: 'في جهازك.',
          sub: 'ستظهر نافذة تأكيد iOS. إذا رفضت مسبقاً، انتقل إلى',
          subBold: 'الإعدادات ← Safari ← الحركة والاتجاه',
          sub2: 'لإعادة تفعيل الوصول.',
          btn: '🧭 السماح بالبوصلة',
        },
        de: {
          title: 'Berechtigung erforderlich',
          body: 'Um die Animation anzuzeigen, benötigt die App Zugriff auf den',
          bold: 'Kompass',
          body2: 'Ihres Geräts.',
          sub: 'Ein iOS-Bestätigungsdialog wird angezeigt. Falls Sie bereits abgelehnt haben, gehen Sie zu',
          subBold: 'Einstellungen → Safari → Bewegung & Ausrichtung',
          sub2: 'um den Zugriff wieder zu aktivieren.',
          btn: '🧭 Kompass erlauben',
        },
      };
      return texts[this.lang] || texts.fr;
    },
    arIntroText() {
      const word = this.pointData.arSearchWord || '';
      const map = {
        fr: {
          line1: 'Restez où vous êtes — ne vous déplacez pas !',
          line2: `Cherchez le/la ${word} en bougeant votre téléphone d'après les flèches ci-dessous.`,
          btn: 'Compris !',
        },
        he: {
          line1: 'הישארו במקומכם — אל תזזו!',
          line2: `חפשו את ה${word} על ידי הזזת הטלפון לפי החיצים שמתחת.`,
          btn: 'הבנתי!',
        },
        en: {
          line1: 'Stay where you are — don\'t move!',
          line2: `Look for the ${word} by moving your phone according to the arrows below.`,
          btn: 'Got it!',
        },
        ar: {
          line1: 'ابقَ في مكانك — لا تتحرك!',
          line2: `ابحث عن ${word} بتحريك هاتفك وفقاً للأسهم أدناه.`,
          btn: 'فهمت!',
        },
        de: {
          line1: 'Bleiben Sie, wo Sie sind — bewegen Sie sich nicht!',
          line2: `Suchen Sie ${word} indem Sie Ihr Telefon den Pfeilen unten entsprechend bewegen.`,
          btn: 'Verstanden!',
        },
      };
      return map[this.lang] || map.fr;
    },
    audioDoneText() {
      const map = {
        fr: 'Si vous avez écouté tout l\'audio, continuez en appuyant sur ce bouton.',
        he: 'אם האזנתם לכל ההקלטה, לחצו על כפתור זה כדי להמשיך.',
        en: 'If you\'ve listened to the full audio, tap this button to continue.',
        ar: 'إذا استمعت إلى التسجيل كاملاً، اضغط على هذا الزر للمتابعة.',
        de: 'Wenn Sie die gesamte Aufnahme gehört haben, tippen Sie auf diese Schaltfläche.',
      };
      return map[this.lang] || map.fr;
    },
    searchHintText() {
      const w = this.pointData.arSearchWord || '';
      const prefixes = {
        fr: 'Cherchez le/la ',
        he: 'חפשו את ',
        ar: 'ابحثوا عن ',
        en: 'Look for the ',
        de: 'Suchen Sie ',
      };
      const prefix = prefixes[this.lang] || prefixes.fr;
      return prefix + w;
    },
  },

  watch: {
    facingSouth(val) {
      if (val && this.audioUrl) {
        this.$nextTick(() => {
          const a = this.$refs.audio;
          if (a && a.paused) {
            a.play().catch(() => {});
            this.playing = true;
            a.addEventListener('ended', () => {
              this.playing = false;
              this.audioEnded = true;
            }, { once: true });
          }
        });
      }
    },
  },

  mounted() {
    const base  = this.pointData.arVideoUrl || '';
    const count = Math.max(1, Math.min(3, parseInt(this.pointData.arGifCount) || 1));
    this.gifSpacing = parseInt(this.pointData.arGifSpacing) || 0;

    if (base) {
      this.gifSrcs = [base];
      for (let i = 1; i < count; i++) {
        setTimeout(() => {
          this.gifSrcs.push(base + '?t=' + (Date.now() + i));
        }, i * 1000);
      }
    }

    // iOS requires a user gesture — show prompt before starting anything
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      this.showIosPrompt = true;
    } else {
      this.startCamera();
      this._startCompassListening();
    }
  },

  beforeUnmount() {
    this.stopCamera();
    this.teardownCompass();
  },

  methods: {
    /* ── iOS prompt handler ── */
    async handleIosPrompt() {
      this.showIosPrompt = false;
      try {
        const perm = await DeviceOrientationEvent.requestPermission();
        if (perm === 'granted') {
          this._startCompassListening();
        } else {
          this.facingSouth = true; // fallback: always show flames
        }
      } catch (e) {
        this.facingSouth = true;
      }
      this.startCamera(); // start camera after user gesture (needed for iOS autoplay too)
    },

    /* ── Camera ── */
    async startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        this.camStream = stream;
        this.$refs.video.srcObject = stream;
      } catch (e) {
        console.error('Camera error:', e);
        const camErrors = {
          fr: 'Caméra non disponible.',
          he: 'המצלמה אינה זמינה.',
          en: 'Camera unavailable.',
          ar: 'الكاميرا غير متاحة.',
          de: 'Kamera nicht verfügbar.',
        };
        this.errorMsg = camErrors[this.lang] || camErrors.fr;
      }
    },

    stopCamera() {
      if (this.camStream) {
        this.camStream.getTracks().forEach(t => t.stop());
        this.camStream = null;
      }
    },

    /* ── Audio ── */
    togglePlay() {
      const a = this.$refs.audio;
      if (!a) return;
      if (a.paused) { a.play(); this.playing = true; }
      else          { a.pause(); this.playing = false; }
    },

    restartAudio() {
      const a = this.$refs.audio;
      if (!a) return;
      a.currentTime = 0;
      a.play();
      this.playing = true;
    },

    /* ── Compass ── */
    _startCompassListening() {
      const handler = (e) => {
        let heading = null;
        if (e.webkitCompassHeading !== undefined && e.webkitCompassHeading !== null) {
          heading = e.webkitCompassHeading;
        } else if (e.absolute && e.alpha !== null) {
          // alpha increases counter-clockwise, compass is clockwise — mirror it
          heading = (360 - e.alpha) % 360;
        }
        if (heading !== null) {
          this.compassAvailable = true;
          this.compassHeading = heading;
          const target = this.pointData.arDirection ?? 180;
          const diff = ((heading - target + 360) % 360);
          const normalizedDiff = diff > 180 ? 360 - diff : diff;
          this.facingSouth = normalizedDiff <= 30;
        }
      };

      window.addEventListener('deviceorientationabsolute', handler, true);
      window.addEventListener('deviceorientation', handler, true);
      this._compassHandler = handler;

      // If no compass event after 3s (desktop / no sensor) → always show flames
      setTimeout(() => {
        if (!this.compassAvailable) this.facingSouth = true;
      }, 3000);
    },

    teardownCompass() {
      if (this._compassHandler) {
        window.removeEventListener('deviceorientationabsolute', this._compassHandler, true);
        window.removeEventListener('deviceorientation', this._compassHandler, true);
        this._compassHandler = null;
      }
    },
  },
};
</script>

<style scoped>
.ar-wrapper {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: #000;
}

/* ── iOS permission prompt ── */
.ios-prompt {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  padding: 24px;
}

.ios-prompt-card {
  background: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 32px 24px;
  max-width: 340px;
  text-align: center;
  color: #fff;
}

.ios-prompt-icon {
  font-size: 56px;
  margin-bottom: 16px;
}

.ios-prompt-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px;
}

.ios-prompt-text {
  font-size: 15px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 12px;
}

.ios-prompt-sub {
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 24px;
}

.ios-prompt-btn {
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: #0a84ff;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

/* ── Camera ── */
.ar-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ── Flames: grow from bottom + fade in over 2s ── */
.ar-gifs {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  margin-bottom: 33%;
  overflow: visible;
  padding: 0 16px;
  box-sizing: border-box;

  transform: scaleY(0);
  transform-origin: bottom center;
  opacity: 0;
  transition: transform 2s ease, opacity 2s ease;
}

.ar-gifs.flames-visible {
  transform: scaleY(1);
  opacity: 1;
}

/* No-grow mode: skip scale, only fade in */
.ar-gifs.ar-no-grow {
  transform: scaleY(1);
  transition: opacity 0.6s ease;
}
.ar-gifs.ar-no-grow.flames-visible {
  transform: scaleY(1);
  opacity: 1;
}

.ar-gif {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  object-fit: contain;
}

.ar-error-bar {
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 9999;
  background: rgba(180, 0, 0, 0.85);
  color: #fff;
  max-width: 85vw;
  text-align: center;
}

.ar-audio-controls {
  position: absolute;
  top: 75px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  gap: 10px;
}

.ar-audio-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Direction guide ── */
.ar-guide {
  position: absolute;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.ar-guide-arc {
  width: 120px;
  height: 120px;
  filter: drop-shadow(0 0 12px rgba(255,255,255,0.4));
}

.ar-guide-arrow {
  width: 80px;
  height: 54px;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
}

.ar-anim-left {
  animation: bounce-left 0.9s ease-in-out infinite;
}
.ar-anim-right {
  animation: bounce-right 0.9s ease-in-out infinite;
}

@keyframes bounce-left {
  0%, 100% { transform: translateX(0); }
  50%       { transform: translateX(-10px); }
}
@keyframes bounce-right {
  0%, 100% { transform: translateX(0); }
  50%       { transform: translateX(10px); }
}

.ar-guide-text {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  text-shadow: 0 1px 6px rgba(0,0,0,0.8);
  text-align: center;
  letter-spacing: 0.3px;
  font-family: VarelaRound, system-ui, sans-serif;
}

.ar-guide-scan {
  font-size: 52px;
  animation: scan-swing 1.4s ease-in-out infinite;
}

@keyframes scan-swing {
  0%, 100% { transform: rotate(-20deg); }
  50%       { transform: rotate(20deg); }
}

.ar-search-hint {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  padding: 10px 24px;
  border-radius: 50px;
  text-align: center;
  white-space: nowrap;
  backdrop-filter: blur(6px);
  letter-spacing: 0.3px;
}

/* ── AR intro overlay ────────────────────────────────────── */
.ar-intro-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 950;
  padding: 24px 16px;
}
.ar-intro-card {
  background: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 20px;
  padding: 26px 22px 20px;
  max-width: 340px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  font-family: VarelaRound, sans-serif;
  color: white;
}
.ar-intro-icon {
  font-size: 38px;
  line-height: 1;
}
.ar-intro-line1 {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #ffcc00;
}
.ar-intro-line2 {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.88);
}
.ar-intro-btn {
  width: 100%;
  padding: 13px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, #38b2f8, #1565c0);
  color: white;
  font-family: VarelaRound, sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
}

/* ── Audio done hint ─────────────────────────────────────── */
.ar-audio-done {
  position: absolute;
  top: 130px;
  left: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  direction: ltr;
  gap: 2px;
  z-index: 920;
  animation: ar-done-in 0.4s ease-out;
  pointer-events: none;
}
.ar-audio-done-arrow {
  width: 40px;
  height: 50px;
  align-self: flex-start;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 6px;
  box-sizing: content-box;
}
.ar-audio-done-text {
  margin: 0;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 12px 16px;
  color: white;
  font-family: VarelaRound, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.55;
  text-align: start;
  max-width: 220px;
}
@keyframes ar-done-in {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
